import React, { KeyboardEvent } from "react";
import { Box, Tooltip, Typography } from "@mui/material";

interface NameMatchStampProps {
  matched: boolean;
  onClick?: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Compact rubber-stamp badge for bank KYC name-match outcome.
 * Shown in the Bank Account Proof card header so it does not obscure the preview.
 * When onClick is provided, the stamp opens the name-match explanation dialog.
 */
function NameMatchStamp({
  matched,
  onClick,
  disabled = false,
  disabledReason,
}: NameMatchStampProps) {
  const label = matched ? "Name Matched" : "Name Mismatch";
  const color = matched ? "#1B7A3D" : "#C62828";
  const bg = matched ? "rgba(27, 122, 61, 0.12)" : "rgba(198, 40, 40, 0.14)";
  const interactive = Boolean(onClick) && !disabled;
  const tooltip = disabled
    ? disabledReason || "Names unavailable for this customer"
    : matched
      ? "Why did this name match?"
      : "Why is this a mismatch?";

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.();
    }
  };

  const stamp = (
    <Box
      aria-label={interactive ? `${label}. ${tooltip}` : label}
      role={interactive ? "button" : "status"}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={handleKeyDown}
      sx={{
        flexShrink: 0,
        alignSelf: "center",
        userSelect: "none",
        transform: "rotate(-8deg)",
        px: 1.25,
        py: 0.4,
        border: "2px solid",
        borderColor: color,
        borderRadius: 0.75,
        bgcolor: bg,
        boxShadow: `inset 0 0 0 1px ${color}`,
        textAlign: "center",
        ...(interactive
          ? {
              cursor: "pointer",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                transform: "rotate(-8deg) translateY(-1px)",
                boxShadow: `inset 0 0 0 1px ${color}, 0 2px 6px rgba(0,0,0,0.12)`,
              },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 3,
              },
            }
          : {
              pointerEvents: onClick ? "auto" : "none",
              opacity: disabled ? 0.55 : 1,
              cursor: disabled ? "not-allowed" : "default",
            }),
      }}
    >
      <Typography
        component="span"
        sx={{
          display: "block",
          color,
          fontWeight: 800,
          fontSize: "0.7rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          lineHeight: 1.2,
          fontFamily: '"Courier New", Courier, monospace',
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (!onClick) return stamp;

  return (
    <Tooltip title={tooltip}>
      <span style={{ display: "inline-flex" }}>{stamp}</span>
    </Tooltip>
  );
}

export default NameMatchStamp;
